import { useState, useSyncExternalStore } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Modal,
  Select,
  Text,
} from "@shopify/polaris";
import {
  createImportCommands,
  type ImportAction,
} from "../../../../packages/shared/customer-import/client";
import {
  queueAccountKey,
  type QueueAccount,
} from "../../../../packages/shared/shopify-queue/client";
export type ImportRun = {
  id: string;
  connection_id: string;
  domain: string;
  source: string;
  state: string;
  items_imported: number;
  items_seen: number;
  skipped_count: number;
  pages_imported: number;
  revision: number;
  attempts: number;
  error: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
};
export type ImportData = {
  stores: { id: string; domain: string; connected: number }[];
  selected_connection_id: string | null;
  connection: { id: string; domain: string; connected: number } | null;
  schedule: {
    connection_id: string;
    domain: string;
    enabled: number;
    revision: number;
    interval_minutes: number;
    next_run_at: string | null;
  } | null;
  runs: ImportRun[];
  total: number;
  nextCursor: number | null;
  legacySchedules: unknown[];
};
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
const commands = new Map<string, ReturnType<typeof createImportCommands>>();
const storage = {
  get length() {
    return window.localStorage.length;
  },
  key: (i: number) => window.localStorage.key(i),
  getItem: (k: string) => window.localStorage.getItem(k),
  setItem: (k: string, v: string) => window.localStorage.setItem(k, v),
  removeItem: (k: string) => window.localStorage.removeItem(k),
};
export function ImportControls({
  data,
  account,
  canImport,
  canSchedule,
  onSaved,
}: {
  data: ImportData;
  account: QueueAccount;
  canImport: boolean;
  canSchedule: boolean;
  onSaved: () => void;
}) {
  const key = JSON.stringify([base, queueAccountKey(account), data.selected_connection_id]);
  if (!commands.has(key))
    commands.set(key, createImportCommands(base, account, storage, data.selected_connection_id || undefined));
  const command = commands.get(key)!;
  const state = useSyncExternalStore(command.subscribe, command.snapshot),
    [review, setReview] = useState<ImportAction | null>(null),
    [configure, setConfigure] = useState(false),
    [enabled, setEnabled] = useState(!!data.schedule?.enabled),
    [interval, setInterval] = useState(
      String(data.schedule?.interval_minutes || 60),
    );
  const actionLocked = state.busy || !!state.pending || !canImport,
    locked = actionLocked || !data.connection?.connected;
  const action = (
    value: Partial<ImportAction> & { action: ImportAction["action"] },
  ) => {
    if (!data.connection && !data.schedule && !value.connection_id) return;
    setReview({
      connection_id: data.connection?.id || data.schedule?.connection_id || "",
      domain: data.connection?.domain || data.schedule?.domain || "",
      revision: 0,
      ...value,
    });
  };
  const send = async () => {
    if (!review) return;
    const value = review;
    setReview(null);
    await command.submit(value);
    if (command.snapshot().success) onSaved();
  };
  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            Customer imports
          </Text>
          <Text as="p">
            Import customers from{" "}
            {data.connection?.domain || "your connected Shopify store"}. Work
            continues after you close this page. Physical stock, reservations,
            costs and warehouse settings stay unchanged.
          </Text>
          <Text as="p">
            Customer contact details, default address, tags and spending totals
            refresh from Shopify. Removed personal details and older source
            versions are skipped. Missing customers are kept; this import does
            not delete customer records.
          </Text>
          {!data.connection?.connected && (
            <Banner tone="warning">
              Connect a Shopify store before importing customers.
            </Banner>
          )}
          {state.error && <Banner tone="critical">{state.error}</Banner>}
          {state.success && <Banner tone="success">{state.success}</Banner>}
          {state.pending && (
            <Banner tone="warning">
              <BlockStack gap="200">
                <Text as="p">
                  A saved import action for {JSON.parse(state.pending.body).domain} needs a confirmed reply. It remains
                  saved after you close the browser.
                </Text>
                <Button
                  loading={state.busy}
                  onClick={() =>
                    void command.retry().then(() => {
                      if (command.snapshot().success) onSaved();
                    })
                  }
                >
                  Retry saved action
                </Button>
              </BlockStack>
            </Banner>
          )}
          <Button disabled={locked} onClick={() => action({ action: "start" })}>
            Review customer import
          </Button>
          <Text as="p">
            {data.schedule?.enabled
              ? `Automatic import every ${data.schedule.interval_minutes} minutes. Next check: ${data.schedule.next_run_at || "pending"} UTC.`
              : "Automatic customer imports are off."}
          </Text>
          <Text as="p">
            An active import finishes its pages before another starts for this store. Resolve a
            failed import before starting another for the same store.
          </Text>
          <Button
            disabled={
              actionLocked ||
              !canSchedule ||
              (!data.connection && !data.schedule)
            }
            onClick={() => {
              setEnabled(!!data.schedule?.enabled);
              setInterval(String(data.schedule?.interval_minutes || 60));
              setConfigure(true);
            }}
          >
            Configure customer schedule
          </Button>
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            Import history
          </Text>
          <Text as="p">
            {data.total} saved imports. Counts show customers saved, checked and
            skipped.
          </Text>
          {data.runs.map((run) => (
            <BlockStack key={run.id} gap="200">
              <Text as="h3" variant="headingSm">
                {
                  (
                    {
                      pending: "Waiting",
                      running: "Processing",
                      failed: "Needs review",
                      completed: "Completed",
                      cancelled: "Cancelled",
                      privacy_removed: "Stopped for privacy removal",
                    } as Record<string, string>
                  )[run.state]
                }{" "}
                · {run.source === "schedule" ? "Scheduled" : "Staff requested"}{" "}
                · {run.created_at} UTC
              </Text>
              <Text as="p">
                {run.items_imported} customers saved; {run.items_seen} checked;{" "}
                {run.skipped_count} skipped in {run.pages_imported} pages. Last
                update: {run.updated_at} UTC.
              </Text>
              {run.error && <Banner tone="warning">{run.error}</Banner>}
              <InlineStack gap="200">
                {run.state === "failed" && (
                  <Button
                    disabled={locked}
                    onClick={() =>
                      action({
                        action: "retry",
                        run_id: run.id,
                        revision: run.revision,
                        connection_id: run.connection_id,
                        domain: run.domain,
                      })
                    }
                  >
                    Review retry
                  </Button>
                )}
                {["pending", "running", "failed"].includes(run.state) && (
                  <Button
                    disabled={actionLocked}
                    onClick={() =>
                      action({
                        action: "cancel",
                        run_id: run.id,
                        revision: run.revision,
                        connection_id: run.connection_id,
                        domain: run.domain,
                      })
                    }
                  >
                    Review cancellation
                  </Button>
                )}
              </InlineStack>
            </BlockStack>
          ))}
          {!data.runs.length && (
            <Text as="p">No customer imports have been requested.</Text>
          )}
        </BlockStack>
      </Card>
      <Modal
        open={configure}
        title="Customer import schedule"
        onClose={() => setConfigure(false)}
        primaryAction={{
          content: "Review schedule",
          onAction: () => {
            setConfigure(false);
            action({
              action: "schedule",
              revision: data.schedule?.revision || 0,
              enabled,
              interval_minutes: Number(interval),
            });
          },
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setConfigure(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Checkbox
              label="Enable automatic customer imports"
              checked={enabled}
              onChange={setEnabled}
            />
            <Select
              label="Import interval"
              value={interval}
              options={[15, 30, 60, 120, 240, 360, 720, 1440].map((v) => ({
                label: v < 60 ? `Every ${v} minutes` : `Every ${v / 60} hours`,
                value: String(v),
              }))}
              onChange={setInterval}
            />
            <Text as="p">
              This schedule uses your current customer import access. Removing
              that access stops future work. Changing or disabling the schedule
              stops later pages of its active import.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={!!review}
        title="Review customer import action"
        onClose={() => setReview(null)}
        primaryAction={{
          content: "Confirm import action",
          disabled:
            review?.action === "cancel" ||
            (review?.action === "schedule" && !review.enabled)
              ? actionLocked
              : locked,
          onAction: () => void send(),
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setReview(null) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">Store: {review?.domain}</Text>
            <Text as="p">
              {review?.action === "start"
                ? "Queue a full customer import. Contact and customer lookup details refresh from Shopify. Warehouse orders and stock remain unchanged."
                : review?.action === "schedule"
                  ? `${review.enabled ? "Enable" : "Disable"} automatic customer imports${review.enabled ? ` every ${review.interval_minutes} minutes` : ""}.`
                  : review?.action === "retry"
                    ? "Continue the selected import from its last saved customer page."
                    : "Cancel the remaining work for this import. Completed customer pages remain saved."}
            </Text>
            <Text as="p">
              Shopify sends customer details in pages. Customers can change
              during a long import. Completed means every requested page was
              checked. This action sends no customer messages.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
}
